import React, { useState } from 'react';
import { useFavorites } from '../hooks/useFavorites';
import { showToast } from './Toaster';
import {
  FavoriteVessel,
  FavoriteLocation,
} from '../services/favoritesService';

interface FavoriteButtonProps {
  type: 'vessel' | 'location';
  id: string;
  name: string;
  registreringsmerke?: string;
  locationName?: string;
  locationType?: string;
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
}

/**
 * FavoriteButton Component
 * Reusable star icon button for toggling favorite status
 * Grey outline (not favorited) → Yellow filled (favorited)
 */
const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  type,
  id,
  name,
  registreringsmerke,
  locationName,
  locationType,
  size = 'medium',
  showLabel = false,
}) => {
  const { toggleVessel, toggleLocation, isVesselFavorited, isLocationFavorited } = useFavorites();
  const [isAnimating, setIsAnimating] = useState(false);

  const isFavorited =
    type === 'vessel'
      ? isVesselFavorited(registreringsmerke || id)
      : isLocationFavorited(id);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);

    if (type === 'vessel') {
      const vessel: FavoriteVessel = {
        id: registreringsmerke || id,
        name,
        registreringsmerke: registreringsmerke || id,
        savedAt: new Date().toISOString(),
      };
      const wasAdded = toggleVessel(vessel);
      if (wasAdded) {
        showToast('Lagt til i favoritter', 'success');
      } else {
        showToast('Fjernet fra favoritter', 'info');
      }
    } else {
      const location: FavoriteLocation = {
        id,
        name: locationName || name,
        type: locationType || 'location',
        savedAt: new Date().toISOString(),
      };
      const wasAdded = toggleLocation(location);
      if (wasAdded) {
        showToast('Lagt til i favoritter', 'success');
      } else {
        showToast('Fjernet fra favoritter', 'info');
      }
    }
  };

  // Size mappings
  const sizeMap = {
    small: { fontSize: '16px', padding: '4px 8px' },
    medium: { fontSize: '20px', padding: '6px 12px' },
    large: { fontSize: '28px', padding: '8px 16px' },
  };

  const starStyle: React.CSSProperties = {
    ...sizeMap[size],
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: sizeMap[size].padding,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'transform 0.2s ease, color 0.2s ease',
    transform: isAnimating ? 'scale(1.3)' : 'scale(1)',
    color: isFavorited ? '#FFD700' : '#999',
    ...(isFavorited && { textShadow: '0 0 4px rgba(255, 215, 0, 0.3)' }),
  };

  const hoverStyle: React.CSSProperties = {
    ...starStyle,
    color: isFavorited ? '#FFD700' : '#666',
  };

  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <button
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={isHovered ? hoverStyle : starStyle}
      title={isFavorited ? 'Fjern fra favoritter' : 'Legg til i favoritter'}
      aria-label={isFavorited ? 'Fjern fra favoritter' : 'Legg til i favoritter'}
    >
      {isFavorited ? '★' : '☆'}
      {showLabel && (
        <span style={{ marginLeft: '6px', fontSize: '14px' }}>
          {isFavorited ? 'Favoritt' : 'Legg til'}
        </span>
      )}
    </button>
  );
};

export default FavoriteButton;
